import { Dispatch, ReactElement, SetStateAction } from 'react';
import { MenuItem, Drawer, styled } from '@mui/material';
import { StyledSelect, ValueLabel } from '../common';
import {
  ISearchableProperty,
  properties as propertiesA,
  diProperties,
  byLabel,
  sortable,
  csaSortPropertyMap,
  labelP1,
  labelP2,
  renderP1,
  renderP2,
  enableP2,
  IRequestStatus,
} from '.';
import { pathOr, always, path, split } from 'rambda';
import { map, filter, sortBy as fSortBy } from 'fp-ts/Array';
import { flow } from 'fp-ts/function';

export const RequestStatusFilterSortStack = ({
  di,
  properties,
  sortBy,
  sortDirection,
  openDrawer,
  searchBy,
  searchP1,
  searchP2,
  toggleSort,
  toggleDrawer,
  search,
  setSearchBy,
  setSearchP1,
  setSearchP2,
}: {
  di: boolean;
  properties: ValueLabel<keyof IRequestStatus>[];
  sortBy: string;
  sortDirection: 'asc' | 'desc';
  openDrawer: boolean;
  searchBy: string;
  searchP1: string;
  searchP2: string;
  toggleSort: (name: string) => void;
  toggleDrawer: (open: boolean) => (event: any) => void;
  search: () => void;
  setSearchBy: Dispatch<SetStateAction<string>>;
  setSearchP1: Dispatch<SetStateAction<string>>;
  setSearchP2: Dispatch<SetStateAction<string>>;
}) => {
  return (
    <Drawer anchor="left" open={openDrawer} onClose={toggleDrawer(false)}>
      <FilterSortWrapper>
        <label id="sort-by">Sort By:</label>
        <StyledSelect
          labelId="sort-by"
          value={sortBy + ':' + sortDirection}
          onChange={flow(
            pathOr('', 'target.value'),

            split(':'),
            as => {
              toggleSort(as[0]);
            },
            () => toggleDrawer(false)({})
          )}
          variant="outlined">
          {flow(
            filter<ValueLabel<keyof IRequestStatus>>(({ value }) =>
              sortable(csaSortPropertyMap)(value)
            ),
            fSortBy([byLabel]),
            map<ValueLabel<keyof IRequestStatus>, ReactElement[]>(
              ({ value, label }) => [
                <MenuItem value={value + ':asc'}>{label}: A to Z</MenuItem>,
                <MenuItem value={value + ':desc'}>{label}: Z to A</MenuItem>,
              ]
            )
          )(properties)}
        </StyledSelect>

        <label id="search-by">Search By:</label>
        <StyledSelect
          labelId="search-by"
          value={searchBy}
          onChange={flow(
            pathOr('', 'target.value'),
            setSearchBy,
            always(''),
            setSearchP1,
            always(''),
            setSearchP2
          )}
          variant="outlined">
          {flow(
            filter<ISearchableProperty>(pathOr(false, 'searchable')),
            fSortBy([byLabel]),
            map<ISearchableProperty, ReactElement>(({ value, label }) => (
              <MenuItem value={value}>{label}</MenuItem>
            ))
          )(di ? diProperties : propertiesA)}
        </StyledSelect>

        {path<string>(searchBy)(labelP1) !== void 0 && (
          <label id="search-parameter-1">
            {path<string>(searchBy)(labelP1)}
          </label>
        )}

        {renderP1({
          di: false,
          csa: false,
          searchBy,
          searchP1,
          setSearchP1,
        })}

        {enableP2(searchBy) && (
          <>
            <label id="search-parameter-2">
              {path<string>(searchBy)(labelP2)}
            </label>
            {renderP2({
              searchP2,
              setSearchP2,
            })}
          </>
        )}

        <SingleFilter id="search-parameter-help">
          For wildcard match, please type %25 after your input.
        </SingleFilter>
        <SingleFilterBtn onClick={flow(search, () => toggleDrawer(false)({}))}>
          search
        </SingleFilterBtn>
      </FilterSortWrapper>
    </Drawer>
  );
};

const FilterSortWrapper = styled('article')(({ theme }) => ({
  padding: '1rem',

  display: 'grid',
  gridTemplateColumns: '1fr 2fr',
  gap: '1rem',
  alignItems: 'center',
}));

const SingleFilter = styled('label')(({ theme }) => ({
  gridColumn: '1/3',
}));

const SingleFilterBtn = styled('button')(({ theme }) => ({
  gridColumn: '1/3',
}));
